import React from 'react';
import styled from 'styled-components';

interface ModernTabsProps {
  children: React.ReactNode;
  className?: string;
}

interface ModernTabProps {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

const TabsWrapper = styled.div`
  display: flex;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(148, 163, 184, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
  }
`;

const TabButton = styled.button<ModernTabProps>`
  flex: 1;
  padding: 14px 20px;
  border: none;
  background: ${props => {
    if (props.active) {
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
    return 'transparent';
  }};
  color: ${props => {
    if (props.disabled) return '#94a3b8';
    if (props.active) return 'white';
    return '#64748b';
  }};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '600' : '500'};
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  
  &:hover:not(:disabled) {
    background: ${props => {
      if (props.active) {
        return 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)';
      }
      return 'rgba(255, 255, 255, 0.7)';
    }};
    color: ${props => props.active ? 'white' : '#475569'};
    transform: translateY(-1px);
    box-shadow: ${props => props.active 
      ? '0 8px 25px -8px rgba(102, 126, 234, 0.4)' 
      : '0 4px 12px -4px rgba(0, 0, 0, 0.1)'
    };
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  ${props => props.active && `
    box-shadow: 0 8px 25px -8px rgba(102, 126, 234, 0.3);
    
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      right: 2px;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      border-radius: 10px;
    }
  `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  font-size: 1rem;
`;

export const ModernTabsContainer: React.FC<ModernTabsProps> = ({ children, className }) => {
  return (
    <TabsWrapper className={className}>
      {children}
    </TabsWrapper>
  );
};

export const ModernTab: React.FC<ModernTabProps> = ({ 
  active, 
  disabled, 
  onClick, 
  children, 
  icon 
}) => {
  return (
    <TabButton
      active={active}
      disabled={disabled}
      onClick={onClick}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      {children}
    </TabButton>
  );
};

export const ModernTabContent = styled.div`
  padding: 32px 0 24px 0;
`;